import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Textarea from "../../../components/ui/Textarea";
import { Checkbox } from "../../../components/ui/Checkbox";
import ProductImageUpload from "./ProductImageUpload";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// fix leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Map marker component
const LocationMarker = ({ position, setPosition, searchPosition }) => {
  const map = useMap();
  useEffect(() => {
    if (searchPosition) map.flyTo(searchPosition, 15);
  }, [searchPosition]);
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });
  return position ? <Marker position={position} /> : null;
};

const CarListingForm = ({
  isOpen,
  listing,
  onClose,
  onSave,
  language = "ar",
}) => {
  const {
    vehiclesList,
    gearTypeList,
    fuelTypeList,
    colorsList,
    governoratesList,
    carTypesList,
    carModelsList,
    importingCountriesList,
  } = useSelector((state) => state.general);

  const { usersList, metaUsers } = useSelector((state) => state.users);

  const labels = {
    title: {
      ar: listing ? "تعديل السيارة" : "إضافة سيارة",
      en: listing ? "Edit Car Listing" : "Add Car Listing",
    },
    brand: { ar: "ماركة السيارة", en: "Car Brand" },
    model: { ar: "موديل السيارة", en: "Model" },
    year: { ar: "سنة الصنع", en: "Year" },
    status: { ar: "حالة السيارة", en: "Status" },
    old: { ar: "مستعملة", en: "Old" },
    new: { ar: "جديدة", en: "New" },
    distance: { ar: "المسافة المقطوعة", en: "Distance Driven" },
    fuel: { ar: "نوع الوقود", en: "Fuel Type" },
    gear: { ar: "ناقل الحركة", en: "Gear Type" },
    color: { ar: "اللون", en: "Color" },
    area: { ar: "المنطقة", en: "Area" },
    price: { ar: "السعر", en: "Price" },
    negotiate: { ar: "قابل للتفاوض", en: "Can Negotiate" },
    description: { ar: "الوصف", en: "Description" },
    contact: { ar: "معلومات التواصل", en: "Contact Info" },
    address: { ar: "العنوان", en: "Address" },
    location: { ar: "الموقع", en: "Location" },
    search: { ar: "بحث", en: "Search" },
    cancel: { ar: "إلغاء", en: "Cancel" },
    save: { ar: "حفظ", en: "Save" },
  };

  const [formData, setFormData] = useState({
    user_id: "",
    car_brand_id: "",
    car_model: "",
    car_model_id: "", // جديد
    car_year: "",
    from_year: "", // جديد
    to_year: "", // جديد
    car_status: 1,
    distance_driven: "",
    fuel_type_id: "",
    gear_type_id: "",
    car_type_id: "",
    color_id: "",
    price: "",
    price_in_dollars: false, // جديد
    engine_size: "", // جديد
    four_weel: false, // جديد
    leather_interior: false, // جديد
    roof_window: false, // جديد
    importing_country_id: "", // جديد
    can_negotiate: false,
    latitude: "",
    longitude: "",
    images: [],
    description: "",
    contact_information: "",
    address: "",
    area_id: "",
    is_active: true,
  });

  const [position, setPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPosition, setSearchPosition] = useState(null);

  // fill form if editing
  useEffect(() => {
    if (listing) {
      setFormData({
        user_id: listing.user?.id || "",
        car_brand_id: listing.car_brand?.id || "",
        car_model: listing.car_model || "",
        car_year: listing.car_year || "",
        car_status: listing.car_status == "OLD" ? 1 : 0 || 1,
        distance_driven: listing.distance_driven || "",
        fuel_type_id: listing.fuel_type?.id || "",
        gear_type_id: listing.gear_type?.id || "",
        car_type_id: listing.car_type?.id || "",
        color_id: listing.color?.id || "",
        price: listing.price || "",
        can_negotiate: listing.can_negotiate || false,
        latitude: listing.latitude || "",
        longitude: listing.longitude || "",
        images: listing.images || [],
        description: listing.description || "",
        contact_information: listing.contact_information || "",
        address: listing.address || "",
        area_id: listing.area?.id || "",
        is_active: listing.is_active !== undefined ? listing.is_active : true,
        car_model_id: listing.car_model?.id || "",
        from_year: listing.from_year || "",
        to_year: listing.to_year || "",
        price_in_dollars: listing.price_in_dollars || false,
        engine_size: listing.engine_size || "",
        four_weel: listing.four_weel || false,
        leather_interior: listing.leather_interior || false,
        roof_window: listing.roof_window || false,
        importing_country_id: listing.importing_country?.id || "",
      });
      if (listing.latitude && listing.longitude)
        setPosition([
          parseFloat(listing.latitude),
          parseFloat(listing.longitude),
        ]);
    } else {
      setFormData({
        car_brand_id: "",
        car_model: "",
        car_year: "",
        car_status: 1,
        distance_driven: "",
        fuel_type_id: "",
        gear_type_id: "",
        car_type_id: "",
        color_id: "",
        price: "",
        can_negotiate: false,
        latitude: "",
        longitude: "",
        images: [],
        description: "",
        contact_information: "",
        address: "",
        area_id: "",
        is_active: true,
      });
      setPosition(null);
    }
  }, [listing]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (position) {
      formData.latitude = position[0];
      formData.longitude = position[1];
    }
    const submitData = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key !== "images")
        submitData.append(
          key,
          [
            "can_negotiate",
            "is_active",
            "four_weel",
            "leather_interior",
            "roof_window",
            "price_in_dollars",
          ].includes(key)
            ? formData[key]
              ? 1
              : 0
            : formData[key],
        );
    });
    formData.images.forEach((img, index) => {
      if (img.file) submitData.append(`images[${index}]`, img.file);
    });
    onSave(submitData);
  };

  const handleSearchLocation = async () => {
    if (!searchQuery.trim()) return;
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
    );
    const data = await res.json();
    if (data.length) {
      const lat = parseFloat(data[0].lat),
        lng = parseFloat(data[0].lon);
      setPosition([lat, lng]);
      setSearchPosition([lat, lng]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card w-full max-w-4xl rounded-xl border max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">{labels.title[language]}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Select
              label="صاحب الإعلان"
              options={usersList?.map((u) => ({
                value: u.enc_id,
                label: u.first_name,
              }))}
              value={formData.user_id}
              onChange={(v) => setFormData({ ...formData, user_id: v })}
            />
            <Select
              label={labels.brand[language]}
              options={vehiclesList?.map((b) => ({
                value: b.id,
                label: b.translations?.ar,
              }))}
              value={formData.car_brand_id}
              onChange={(v) => setFormData({ ...formData, car_brand_id: v })}
            />
            <Select
              label={language === "ar" ? "نوع السيارة" : "Car Type"}
              options={carTypesList?.map((t) => ({
                value: t.id,
                label: t?.translations?.ar,
              }))}
              value={formData.car_type_id}
              onChange={(v) => setFormData({ ...formData, car_type_id: v })}
            />
            <Select
              label={language === "ar" ? "موديل السيارة" : "Car Model"}
              options={carModelsList?.map((m) => ({
                value: m.id,
                label: m?.translations?.ar,
              }))}
              value={formData.car_model_id}
              onChange={(v) => setFormData({ ...formData, car_model_id: v })}
            />
            <Select
              label={language === "ar" ? "بلد الاستيراد" : "Importing Country"}
              options={importingCountriesList?.map((c) => ({
                value: c.id,
                label: c?.translations?.ar,
              }))}
              value={formData.importing_country_id}
              onChange={(v) =>
                setFormData({ ...formData, importing_country_id: v })
              }
            />
            <Input
              label={language === "ar" ? "حجم المحرك" : "Engine Size"}
              value={formData.engine_size}
              onChange={(e) =>
                setFormData({ ...formData, engine_size: e.target.value })
              }
            />
            <Input
              label={language === "ar" ? "من سنة" : "From Year"}
              type="number"
              value={formData.from_year}
              onChange={(e) =>
                setFormData({ ...formData, from_year: e.target.value })
              }
            />

            <Input
              label={language === "ar" ? "إلى سنة" : "To Year"}
              type="number"
              value={formData.to_year}
              onChange={(e) =>
                setFormData({ ...formData, to_year: e.target.value })
              }
            />

            <Input
              label={labels.model[language]}
              value={formData.car_model}
              onChange={(e) =>
                setFormData({ ...formData, car_model: e.target.value })
              }
            />
            <Input
              label={labels.year[language]}
              type="number"
              value={formData.car_year}
              onChange={(e) =>
                setFormData({ ...formData, car_year: e.target.value })
              }
            />
            <Select
              label={labels.status[language]}
              options={[
                { value: 1, label: labels.old[language] },
                { value: 2, label: labels.new[language] },
              ]}
              value={formData.car_status}
              onChange={(v) => setFormData({ ...formData, car_status: v })}
            />
            <Input
              label={labels.distance[language]}
              type="number"
              value={formData.distance_driven}
              onChange={(e) =>
                setFormData({ ...formData, distance_driven: e.target.value })
              }
            />
            <Select
              label={labels.fuel[language]}
              options={fuelTypeList?.map((f) => ({
                value: f.id,
                label: f?.translations?.ar,
              }))}
              value={formData.fuel_type_id}
              onChange={(v) => setFormData({ ...formData, fuel_type_id: v })}
            />
            <Select
              label={labels.gear[language]}
              options={gearTypeList?.map((g) => ({
                value: g.id,
                label: g?.translations?.ar,
              }))}
              value={formData.gear_type_id}
              onChange={(v) => setFormData({ ...formData, gear_type_id: v })}
            />
            <Select
              label={labels.color[language]}
              options={colorsList?.map((c) => ({
                value: c.id,
                label: c?.translations?.ar,
              }))}
              value={formData.color_id}
              onChange={(v) => setFormData({ ...formData, color_id: v })}
            />
            <Select
              label={labels.area[language]}
              options={governoratesList?.map((a) => ({
                value: a.id,
                label: a?.translations?.ar,
              }))}
              value={formData.area_id}
              onChange={(v) => setFormData({ ...formData, area_id: v })}
            />
            <Input
              label={labels.price[language]}
              type="number"
              value={formData.price}
              onChange={(e) =>
                setFormData({ ...formData, price: e.target.value })
              }
            />
            <Checkbox
              label={labels.negotiate[language]}
              checked={formData.can_negotiate}
              onChange={(e) =>
                setFormData({ ...formData, can_negotiate: e.target.checked })
              }
            />
            <Textarea
              label={labels.description[language]}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
            <Input
              label={labels.contact[language]}
              value={formData.contact_information}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  contact_information: e.target.value,
                })
              }
            />
            <Input
              label={labels.address[language]}
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
            />
            <ProductImageUpload
              images={formData.images}
              onChange={(images) => setFormData({ ...formData, images })}
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: "four_weel", label: "دفع رباعي" },
                { key: "leather_interior", label: "فرش جلد" },
                { key: "roof_window", label: "فتحة سقف" },
                { key: "price_in_dollars", label: "السعر بالدولار" },
              ].map((item) => (
                <div
                  key={item.key}
                  onClick={() =>
                    setFormData({
                      ...formData,
                      [item.key]: !formData[item.key],
                    })
                  }
                  className={`cursor-pointer border rounded-xl p-3 text-center transition
        ${
          formData[item.key]
            ? "bg-primary text-white border-primary"
            : "bg-white hover:bg-gray-100"
        }`}
                >
                  <p className="text-sm font-medium">{item.label}</p>
                </div>
              ))}
            </div>
            {/* map */}
            <div>
              <p className="mb-2">{labels.location[language]}</p>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder={labels.search[language]}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button type="button" onClick={handleSearchLocation}>
                  {labels.search[language]}
                </Button>
              </div>
              <div className="h-64">
                <MapContainer
                  center={[33.5, 36.3]}
                  zoom={13}
                  style={{ height: "100%" }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <LocationMarker
                    position={position}
                    setPosition={setPosition}
                    searchPosition={searchPosition}
                  />
                </MapContainer>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                fullWidth
              >
                {labels.cancel[language]}
              </Button>
              <Button type="submit" fullWidth>
                {labels.save[language]}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CarListingForm;
